/**
 * The site's boxes (#804): one list joining JSS machine records to
 * Kubernetes nodes by `ClusterNode.machineId`.
 *
 * This panel used to render the same hardware twice: a "Provisioning Nodes"
 * card of machines (which hid `ready` ones, so a Ready box had no Reset or
 * Reinstall anywhere) above a Kubernetes-only node table that never read
 * `machineId`. Recovery was gated on status, and one "Last seen" column —
 * the SideroLink handshake — made a box with a dead Talos API look alive.
 */
import { useState } from 'react';
import { Server, AlertTriangle } from 'lucide-react';
import type { ClusterNode, Machine } from '@/types';
import { boxIndicators, joinBoxes, type Box, type NodesState } from '@/lib/boxes';
import type { RecoveryAction } from '@/lib/boxRecovery';
import { describeMachineStatus } from '@/lib/machineStatus';
import { RemoveMachineDialog } from '@/components/deployments/RemoveMachineDialog';
import { useFeature } from '@/hooks/useFeature';
import BoxRow from './BoxRow';
import BoxRecoveryDialog from './BoxRecoveryDialog';
import AllowReflashDialog from './AllowReflashDialog';

interface Props {
  /** The site these boxes belong to: what the `netboot` feature flag is evaluated for (#954). */
  siteId?: string;
  nodes: ClusterNode[] | undefined;
  isLoading: boolean;
  /** The site's Kubernetes API could not be reached (no node data). */
  nodesError?: boolean;
  onNodeAction: (node: ClusterNode, action: string) => void;
  machines?: Machine[];
  /** Open the Engineer-tier Talos diagnostics drawer (#248). Caller role-gates it. */
  onNodeDiagnostics?: (node: ClusterNode) => void;
  /** Open the Admin-tier taint editor (#256). */
  onNodeEditTaints?: (node: ClusterNode) => void;
}

export default function NodesPanel({
  siteId,
  nodes,
  isLoading,
  nodesError,
  onNodeAction,
  machines,
  onNodeDiagnostics,
  onNodeEditTaints,
}: Props) {
  const [recovery, setRecovery] = useState<{ box: Box; action: RecoveryAction } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Machine | null>(null);
  const [reflashTarget, setReflashTarget] = useState<Machine | null>(null);
  // Netboot is behind a feature flag, off by default (#954): don't point at
  // a Network Boot card that isn't there.
  const netboot = useFeature('netboot', { siteId });

  const siteMachines = machines ?? [];
  const nodesState: NodesState = nodes ? 'ok' : nodesError ? 'error' : isLoading ? 'loading' : 'error';
  const { boxes, hiddenDecommissioned } = joinBoxes(siteMachines, nodes);
  const now = Date.now();
  const rows = boxes.map((box) => ({ box, indicators: boxIndicators(box, nodesState, now) }));

  if (rows.length === 0 && isLoading) {
    return (
      <div className="bg-surface border border-surface-elevated rounded-xl">
        <div className="p-8 space-y-3 animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-12 bg-surface-elevated rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-surface border border-surface-elevated rounded-xl">
        <div className="text-center py-12 space-y-3">
          <Server size={40} className="mx-auto text-silver-muted/30" />
          <p className="text-silver-muted">No boxes on this site yet</p>
          <p className="text-silver-muted/60 text-xs max-w-md mx-auto">
            {netboot
              ? 'Start a box from this site’s install image (Overview → Boot Images) or netboot it (Overview → Network Boot).'
              : 'Start a box from this site’s install image (Overview → Boot Images).'}{' '}
            It appears here as soon as it connects, and sets itself up.
          </p>
        </div>
      </div>
    );
  }

  const attention = rows.filter(
    ({ box, indicators }) =>
      indicators.some((i) => i.state === 'fail') ||
      (box.machine && describeMachineStatus(box.machine.status).tone === 'error'),
  ).length;

  return (
    <div className="space-y-3">
      <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-elevated flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-medium text-silver-bright">Boxes ({rows.length})</h3>
            <p className="text-xs text-silver-muted mt-0.5">
              Each box&apos;s management tunnel (SideroLink), Talos API and Kubernetes kubelet, checked separately.
            </p>
          </div>
          {attention > 0 && (
            <span className="flex items-center gap-1 text-xs text-status-error" data-testid="boxes-attention">
              <AlertTriangle size={12} />
              {attention} need{attention === 1 ? 's' : ''} attention
            </span>
          )}
        </div>
        {/* Only when a box should be in the cluster: a site still being set
            up has no Kubernetes API to reach yet, and that isn't news. */}
        {nodesState === 'error' && rows.some(({ indicators }) => indicators.some((i) => i.key === 'kubelet' && i.state === 'unknown')) && (
          <div className="px-4 py-2 border-b border-surface-elevated text-xs text-status-warning bg-status-warning/5">
            The site&apos;s Kubernetes API can&apos;t be reached right now, so kubelet health is unknown. Tunnel and
            Talos API health still come from Jeket&apos;s own probes.
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-elevated">
                <th className="px-4 py-3 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Box</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Health</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-silver-muted uppercase tracking-wider">Versions</th>
                <th className="px-4 py-3 w-10"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ box, indicators }) => (
                <BoxRow
                  key={box.key}
                  box={box}
                  indicators={indicators}
                  onRecover={(b, action) => setRecovery({ box: b, action })}
                  onRemove={setRemoveTarget}
                  onAllowReflash={setReflashTarget}
                  onNodeAction={onNodeAction}
                  onNodeDiagnostics={onNodeDiagnostics}
                  onNodeEditTaints={onNodeEditTaints}
                />
              ))}
            </tbody>
          </table>
        </div>
        {hiddenDecommissioned > 0 && (
          <p className="px-4 py-2 text-[11px] text-silver-muted border-t border-surface-elevated">
            {hiddenDecommissioned} decommissioned box{hiddenDecommissioned === 1 ? '' : 'es'} not shown.
          </p>
        )}
      </div>

      {recovery?.box.machine && (
        <BoxRecoveryDialog
          action={recovery.action}
          machine={recovery.box.machine}
          node={recovery.box.node}
          siteMachines={siteMachines}
          onClose={() => setRecovery(null)}
        />
      )}
      {removeTarget && <RemoveMachineDialog machine={removeTarget} onClose={() => setRemoveTarget(null)} />}
      {reflashTarget && <AllowReflashDialog machine={reflashTarget} onClose={() => setReflashTarget(null)} />}
    </div>
  );
}
